// apps/web/src/features/cargo/components/cargoActionCells.tsx
//
// Interactive registry cells (#1232): inline client-tariff edit, the
// client-paid document-modal trigger, and the delete trigger. EVERY control
// stops propagation so it never also opens the row-edit modal. Each cell falls
// back to its plain #1231 rendering when the actor lacks the corresponding
// ability.

import { useState } from 'react';
import type { TFunction } from 'i18next';
import { CARGO_CLIENT_PAYMENT_STATUS_LABELS, type CargoArticleWire } from '@magimp/shared';

import { Button, Icon, Input } from '../../../ui/index.js';
import { normalizeCargoFreeTextInput } from '../numeric.js';
import { formatMoneyString } from '../../../i18n/formatters.js';

type T = TFunction<readonly ['cargo']>;

// Handlers the registry page injects; each is a no-op-free callback that runs
// the corresponding mutation (with its own toast + invalidation).
export interface CargoRowActions {
  onSaveClientTariff: (row: CargoArticleWire, value: string | null) => void;
  onOpenPaymentModal: (row: CargoArticleWire) => void;
  onRequestDelete: (row: CargoArticleWire) => void;
  // Per-row ability predicates (derived from the current actor).
  canEditTariff: (row: CargoArticleWire) => boolean;
  canDelete: boolean;
}

function stop(e: { stopPropagation: () => void }): void {
  e.stopPropagation();
}

/** Inline client-tariff editor (row-scoped). Read-only label otherwise. */
export function CargoClientTariffCell({
  row,
  actions,
}: {
  row: CargoArticleWire;
  actions: CargoRowActions;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(row.transportationTariffForClients ?? '');
  const current = row.transportationTariffForClients ?? '—';

  if (!actions.canEditTariff(row)) return <span>{current}</span>;

  if (!editing) {
    return (
      <Button
        size="sm"
        kind="quiet"
        data-testid="cargo-tariff-edit"
        onClick={(e) => {
          stop(e);
          setValue(row.transportationTariffForClients ?? '');
          setEditing(true);
        }}
      >
        {current}
      </Button>
    );
  }

  return (
    // Pure positional container — stops row-level click propagation only; no
    // user interaction is routed through the div itself.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={stop}
      style={{ display: 'flex', alignItems: 'center', gap: 4 }}
      data-testid="cargo-tariff-editor"
    >
      <Input value={value} onChange={setValue} style={{ width: 90 }} />
      <Button
        size="sm"
        kind="quiet"
        icon="check"
        aria-label="save"
        data-testid="cargo-tariff-save"
        onClick={(e) => {
          stop(e);
          actions.onSaveClientTariff(row, normalizeCargoFreeTextInput(value));
          setEditing(false);
        }}
      />
      <Button
        size="sm"
        kind="quiet"
        icon="x"
        aria-label="cancel"
        onClick={(e) => {
          stop(e);
          setEditing(false);
        }}
      />
    </div>
  );
}

// Deterministic icon order: rejected → pending → accepted.
const DOC_ICON_ORDER: ReadonlyArray<
  [keyof NonNullable<CargoArticleWire['clientPaymentDocCounts']>, string]
> = [
  ['rejected', 'var(--status-error-fg)'],
  ['pending', 'var(--status-warn-fg)'],
  ['accepted', 'var(--status-success-fg)'],
];

const MAX_ICONS = 3;

/**
 * Client-payment cell: the label opens the document-gated payment modal.
 * Shows colored doc-count icons (rejected→pending→accepted, capped at 3),
 * the RUB accepted total, and a «!» rejection marker when present.
 */
export function CargoClientPaymentCell({
  row,
  t,
  actions,
}: {
  row: CargoArticleWire;
  t: T;
  actions: CargoRowActions;
}) {
  const label = CARGO_CLIENT_PAYMENT_STATUS_LABELS[row.paidByClientStatus];
  const counts = row.clientPaymentDocCounts;
  const rubTotal = row.clientPaymentAcceptedRubTotal;

  const hasRejected = (counts?.rejected ?? 0) > 0;

  // Build flat icon list: [color, …] per document in rejected→pending→accepted order.
  const iconList: string[] = counts
    ? DOC_ICON_ORDER.flatMap(([key, color]) => Array<string>(counts[key]).fill(color))
    : [];
  const total = iconList.length;
  const showIcons = total > 0;
  const overflow = total > MAX_ICONS ? total - MAX_ICONS : 0;
  const visibleIcons = overflow > 0 ? iconList.slice(0, MAX_ICONS) : iconList;

  const docLabel =
    counts != null
      ? t('cargo:payment.docCounts', {
          rejected: counts.rejected,
          pending: counts.pending,
          accepted: counts.accepted,
        })
      : undefined;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <Button
        size="sm"
        kind="quiet"
        data-testid="cargo-client-payment-open"
        onClick={(e) => {
          stop(e);
          actions.onOpenPaymentModal(row);
        }}
      >
        {label}
      </Button>
      {rubTotal != null && (
        <span style={{ fontSize: 11, color: 'var(--status-success-fg)' }}>
          {`${formatMoneyString(rubTotal)} ₽`}
        </span>
      )}
      {hasRejected && (
        <span
          data-testid="cargo-client-payment-rejected"
          style={{ color: 'var(--status-error-fg)', fontWeight: 600, fontSize: 13 }}
          aria-label={t('cargo:payment.rejectedMarker')}
          title={t('cargo:payment.rejectedMarker')}
        >
          !
        </span>
      )}
      {showIcons && (
        <span
          data-testid="cargo-payment-doc-icons"
          style={{ display: 'flex', alignItems: 'center', gap: 2 }}
          aria-label={docLabel}
          title={docLabel}
        >
          {visibleIcons.map((color, i) => (
            // WHY: icons are positional color slots; index key is stable and correct here.
            <Icon key={i} name="file-text" size={14} style={{ color }} />
          ))}
          {overflow > 0 && <span style={{ fontSize: 11 }}>{`+${overflow}`}</span>}
        </span>
      )}
    </div>
  );
}

/** Delete trigger: opens the confirm dialog (which shows the article string). */
export function CargoDeleteCell({
  row,
  t,
  actions,
}: {
  row: CargoArticleWire;
  t: T;
  actions: CargoRowActions;
}) {
  if (!actions.canDelete) {
    return (
      <span aria-label={t('cargo:cells.deleteDisabled')} title={t('cargo:cells.deleteDisabled')}>
        <Icon name="trash-2" size={14} />
      </span>
    );
  }
  return (
    <Button
      size="sm"
      kind="quiet"
      icon="trash-2"
      aria-label={t('cargo:actions.delete')}
      data-testid="cargo-delete"
      onClick={(e) => {
        stop(e);
        actions.onRequestDelete(row);
      }}
    />
  );
}
